import React, {useEffect, useState} from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {InstrumentAdapter} from 'instruments/connections/ConnectionConstants';
import {listInstrumentTypes} from 'instruments/connections/ConnectionDAO';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface AdapterSelectorProps {
  className?: string;
  value: string | null;
  onChange: (adapterId: string | null) => void;
  disabled?: boolean;
  disabledTooltip?: string | null;
}

function AdapterSelector(props: AdapterSelectorProps): JSX.Element {
  const [adapters, setAdapters] = useState<Array<InstrumentAdapter> | null>(null);

  useEffect(() => {
    let isMounted = true;
    listInstrumentTypes()
      .then(({instrumentAdapters}) => {
        if (isMounted) {
          setAdapters(instrumentAdapters);
        }
      })
      .catch(() => {
        // Fall out of the loading state on failure so the dropdown renders
        // (with no adapters) instead of leaving the throbber spinning forever.
        if (isMounted) {
          setAdapters([]);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  if (adapters == null) {
    return <CenteredHorizontalThrobber />;
  }

  const sortedAdapters = [...adapters].sort((a, b) => a.displayName.localeCompare(b.displayName));

  const optionDatas = [
    {name: 'None', value: null},
    ...sortedAdapters.map((adapter) => ({
      name: adapter.displayName,
      value: adapter.id,
    })),
  ];

  const selectedAdapter = adapters.find((adapter) => adapter.id === props.value) ?? null;

  const isDisabled =
    props.disabled || !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT});

  return (
    <SelectDropdown
      className={props.className}
      disabled={isDisabled}
      displayName={selectedAdapter == null ? 'Select...' : selectedAdapter.displayName}
      menu={
        <SearchableMenu
          onSelect={props.onChange}
          optionDatas={optionDatas}
          selectedValue={props.value}
          width={Menu.WIDTHS.FULL}
        />
      }
      error={props.disabled ? props.disabledTooltip : undefined}
      errorStyle={props.disabled ? SelectDropdownErrorStyle.TOOLTIP : undefined}
    />
  );
}

export default AdapterSelector;
